import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { AppPage } from "@/stores/EngineStore/enums/AppPage.ts";
import { useEffect, useRef } from "react";

const HISTORY_STATE_KEY = "bookingEnginePage";

// Ordered list of pages for rebuilding history on mount
const PAGE_ORDER: AppPage[] = [
  AppPage.AccommodationPicking,
  AppPage.ExtraOptions,
  AppPage.HolderAndPaymentInfo,
  AppPage.ReservationOverview,
];

/**
 * Syncs the booking engine's internal page navigation with the browser history stack.
 * Allows the browser back button to navigate between booking steps instead of
 * leaving the host page entirely.
 *
 * Only reacts to history entries created by the widget (identified by HISTORY_STATE_KEY).
 * Does not interfere with the host site's routing.
 */
export const useHistorySync = () => {
  const isPopStateNavigation = useRef(false);
  const didMount = useRef(false);

  // On mount: push the initial page (and intermediate steps) onto the history stack.
  // This creates an "escape hatch" — the entry below the first push is the host page's,
  // so pressing back from the initial page will leave the widget.
  //
  // When the widget reloads with a persisted page (e.g., after payment redirect returns
  // to ReservationOverview), we rebuild the full history stack up to that page so
  // back navigation works through all intermediate steps.
  useEffect(() => {
    if (didMount.current) return;
    didMount.current = true;

    const currentPage = useEngineStore.getState().page;
    const currentPageIndex = PAGE_ORDER.indexOf(currentPage);

    if (currentPageIndex > 0) {
      // Rebuild history stack: push all steps up to and including the current page
      for (let i = 0; i <= currentPageIndex; i++) {
        window.history.pushState({ ...window.history.state, [HISTORY_STATE_KEY]: PAGE_ORDER[i] }, "");
      }
    } else {
      // On step 1 or PaymentResult, just push a single entry
      window.history.pushState({ ...window.history.state, [HISTORY_STATE_KEY]: currentPage }, "");
    }
  }, []);

  // Subscribe to store page changes and sync to browser history
  useEffect(() => {
    const unsubscribe = useEngineStore.subscribe((state, prevState) => {
      if (state.page === prevState.page) return;

      if (isPopStateNavigation.current) {
        isPopStateNavigation.current = false;
        return;
      }

      const stateToWrite = { ...window.history.state, [HISTORY_STATE_KEY]: state.page };

      if (state.pageTransitionMode === "replace") {
        window.history.replaceState(stateToWrite, "");
      } else {
        window.history.pushState(stateToWrite, "");
      }
    });

    return unsubscribe;
  }, []);

  // Listen for browser back/forward button
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const targetPage = event.state?.[HISTORY_STATE_KEY] as AppPage | undefined;

      if (!targetPage) {
        // No widget state in this history entry — user navigated past the widget.
        // Let the browser handle it naturally.
        return;
      }

      // Guard: if target page matches current page, no store update is needed.
      // Without this, isPopStateNavigation would be set but never cleared
      // (since the subscribe callback skips same-page transitions).
      const currentPage = useEngineStore.getState().page;
      if (targetPage === currentPage) return;

      // Special case: back from PaymentResult should go to AccommodationPicking (fresh start)
      if (currentPage === AppPage.PaymentResult) {
        window.history.replaceState(
          { ...window.history.state, [HISTORY_STATE_KEY]: AppPage.AccommodationPicking },
          "",
        );
        isPopStateNavigation.current = true;
        useEngineStore.getState().setPage(AppPage.AccommodationPicking, "replace");
        return;
      }

      isPopStateNavigation.current = true;
      useEngineStore.getState().setPage(targetPage, "replace");
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);
};
